import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StateContext, Store } from '@ngxs/store';
import { FeaturePurePipe, injectCan$ } from '@wilson/authorization';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import {
  Activity,
  ActivitySelectionListItem,
  FeatureName,
  GroupedUnassignedUserDataV2,
  Job,
  RoleAction,
  RolePermissionSubject,
  Service,
} from '@wilson/interfaces';
import { ActivitiesDataService } from '@wilson/non-domain-specific/activities-helpers/services';
import { isDebugEnabled } from '@wilson/non-domain-specific/logger/logger';
import {
  createDateTimeWithoutSeconds,
  determineUnassignmentsOverlaps,
  groupActivitiesByServiceAndJob,
} from '@wilson/non-domain-specific/overlap-helpers';
import { endOfDay, startOfDay } from 'date-fns';
import { uniqBy } from 'lodash';
import {
  catchError,
  first,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
  zip,
} from 'rxjs';
import { ShiftTimelineUnassignedRegionStateModel } from '../interfaces';
import { ShiftTimelineDataState } from '../shift-timeline-data/shift-timeline-data.state';
import { TimelineUpdateUnassignedActivities } from './shift-timeline-unassigned-region.actions';
import { NewWilsonJobBucketState } from '@wilson/share/state';
import { NzMessageService } from 'ng-zorro-antd/message';

@Injectable({
  providedIn: 'root',
})
export class TimelineFetchUnassignedActivitiesActionService {
  private store = inject(Store);
  private featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private readonly can$ = injectCan$();
  private featurePurePipe = inject(FeaturePurePipe);
  private activitiesDataService = inject(ActivitiesDataService);
  private translateService = inject(TranslateService);
  private readonly messageService = inject(NzMessageService);

  private hasWilsonShareReadAccess$ = zip([
    this.featureFlagPurePipe.transform('wilson-share-feature'),
    this.can$(RoleAction.Read, RolePermissionSubject.WilsonShare),
    this.featurePurePipe.transform(FeatureName.WilsonShare),
  ]).pipe(
    map(
      ([isEnabledOnLaunchDarkly, hasPermissionToRead, hasWilsonFeature]: [
        boolean,
        boolean,
        boolean,
      ]) => isEnabledOnLaunchDarkly && hasPermissionToRead && hasWilsonFeature,
    ),
    shareReplay(1),
  );

  handleUpdate(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: TimelineUpdateUnassignedActivities,
  ): Observable<ActivitySelectionListItem[]> {
    const currentTimeFrame = this.store.selectSnapshot(
      ShiftTimelineDataState.timeframe,
    );

    ctx.patchState({
      isRefreshingUnassignedActivities: true,
    });
    return this.getUnassignedActivities(action.timeFrame).pipe(
      tap(async (activities: ActivitySelectionListItem[]) => {
        const allActivities = uniqBy(
          [...activities, ...ctx.getState().unassignedActivities],
          'id',
        ) as ActivitySelectionListItem[];
        const activitiesToUpdate = allActivities.filter((activities) => {
          const shiftStartDateTime = new Date(
            activities.startDatetime,
          ).getTime();
          return (
            shiftStartDateTime >= currentTimeFrame.start &&
            shiftStartDateTime <= currentTimeFrame.end
          );
        });
        const updatablePartialState =
          this.getUpdatablePartialStateSync(activitiesToUpdate);

        ctx.patchState({
          ...updatablePartialState,
          isRefreshingUnassignedActivities: false,
        });
      }),
      catchError(() => {
        this.messageService.error(
          this.translateService.instant('general.something_went_wrong'),
        );
        return [];
      }),
    );
  }

  public getUpdatablePartialStateSync(activitiesToUpdate: Activity[]): {
    unassignedServices: Service[];
    unassignedJobs: Job[];
    unassignedActivities: Activity[];
    unassignedActivitiesViewRows: GroupedUnassignedUserDataV2[];
  } {
    const viewRows = determineUnassignmentsOverlaps({
      ...groupActivitiesByServiceAndJob(activitiesToUpdate),
      isDebugEnabled: isDebugEnabled(),
    });

    return {
      unassignedActivitiesViewRows: viewRows,
      ...this.getCommonUpdatablePartialState(activitiesToUpdate),
    };
  }

  private getCommonUpdatablePartialState(activitiesToUpdate: Activity[]): {
    unassignedServices: Service[];
    unassignedJobs: Job[];
    unassignedActivities: Activity[];
  } {
    return {
      unassignedServices: activitiesToUpdate.reduce<Service[]>(
        (services, activity) => {
          if (activity.service) {
            services.push(activity.service);
          }
          return services;
        },
        [],
      ),
      unassignedJobs: activitiesToUpdate.reduce<Job[]>((services, activity) => {
        if (activity.job) {
          services.push(activity.job);
        }
        return services;
      }, []),
      unassignedActivities: activitiesToUpdate,
    };
  }

  private getUnassignedActivities(
    timeFrame: Interval,
  ): Observable<ActivitySelectionListItem[]> {
    const activityRelations = [
      'activityCategory',
      'service',
      'serviceDeviations',
      'profession',
      'startLocation',
      'endLocation',
      'service.labels',
    ];
    const startDate = startOfDay(timeFrame.start);
    const endDate = endOfDay(timeFrame.end);
    const activities = this.hasWilsonShareReadAccess$.pipe(
      switchMap((hasWilsonShareAccess: boolean) =>
        this.activitiesDataService
          .loadUnassignedActivities({
            startDate,
            endDate,
            includeActivitiesFromJobs: hasWilsonShareAccess,
            activityRelations,
            filterOutActivitiesInRequestForProposal: true,
            idsOfItemsInTheBucket: this.store.selectSnapshot(
              NewWilsonJobBucketState.itemIds,
            ),
          })
          .pipe(
            map((activities) => {
              return (
                activities?.map((activity) => {
                  activity.startDatetime = createDateTimeWithoutSeconds(
                    activity.startDatetime,
                  ).toISOString();
                  activity.endDatetime = createDateTimeWithoutSeconds(
                    activity.endDatetime,
                  ).toISOString();
                  return activity;
                }) || []
              );
            }),
          ),
      ),
      first(),
    );
    return activities;
  }
}
